import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'sonner';

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('edilservice_cart');
    if (savedCart) {
      try {
        setCart(JSON.parse(savedCart));
      } catch (error) {
        console.error('Error loading cart:', error);
      }
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('edilservice_cart', JSON.stringify(cart));
  }, [cart]);

  const addToCart = (product, quantity = 1, unitType = 'sacchi') => {
    setCart(prevCart => {
      const existingItem = prevCart.find(
        item => item.product_id === product.product_id && item.unit_type === unitType
      );
      
      if (existingItem) {
        // Update quantity
        return prevCart.map(item =>
          item.product_id === product.product_id && item.unit_type === unitType
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      } else {
        // Add new item with unit_type
        return [...prevCart, { ...product, quantity, unit_type: unitType }];
      }
    });
    
    const unitLabel = unitType === 'bancali' ? `bancale/i (${quantity * 54} sacchi)` : 'sacco/i';
    toast.success(`${quantity} ${unitLabel} di ${product.name} aggiunto al carrello`);
  };

  const removeFromCart = (productId) => {
    setCart(prevCart => prevCart.filter(item => item.product_id !== productId));
    toast.info('Prodotto rimosso dal carrello');
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }

    setCart(prevCart =>
      prevCart.map(item =>
        item.product_id === productId
          ? { ...item, quantity }
          : item
      )
    );
  };

  const clearCart = () => {
    setCart([]);
    toast.info('Carrello svuotato');
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => {
      const finalPrice = (item.price || 0) * (1 - (item.discount_percentage || 0) / 100);
      // Check if product supports bancale and multiply by 54
      const quantityMultiplier = (item.unit_type === 'bancali') ? 54 : 1;
      return total + (finalPrice * item.quantity * quantityMultiplier);
    }, 0);
  };

  const getCartSubtotal = () => {
    // Subtotal without VAT
    return getCartTotal();
  };

  const getCartVAT = () => {
    // VAT 22%
    return getCartTotal() * 0.22;
  };

  const getCartTotalWithVAT = () => {
    // Total with VAT included
    return getCartTotal() * 1.22;
  };

  const getCartCount = () => {
    return cart.reduce((count, item) => {
      const quantityMultiplier = (item.unit_type === 'bancali') ? 54 : 1;
      return count + (item.quantity * quantityMultiplier);
    }, 0);
  };

  const value = {
    cart,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartSubtotal,
    getCartVAT,
    getCartTotalWithVAT,
    getCartCount
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
